import { useCallback, useEffect, useState } from "react";
import {
  getAufmassunterlagen,
  getMontageAbAvailability,
  getMontageunterlagen,
  getWerkstattunterlagen,
  isAufmassPackItem,
  isWerkstattPackItem,
  type Montageunterlage,
} from "@/lib/projectOverview";
import { getAuftragAufmass } from "@/lib/aufmass";
import { listAuftragStuecklisten, type Stueckliste } from "@/lib/stuecklisten";
import {
  COVER_PRINT_LOG_CHANGED_EVENT,
  coverPrintActor,
  coverPrintKindLabel,
  coverPrintPlace,
  getCoverPrintLog,
  latestCoverPrint,
  type CoverPrintLogEntry,
} from "@/lib/coverPrintLog";
import { formatDateTime } from "@/lib/format";
import { userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import { MontageunterlagenDialog } from "@/projectOverview/MontageunterlagenDialog";
import { AufmassDialog } from "@/projectOverview/AufmassDialog";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { UnterlagenPackCard } from "@/projectOverview/UnterlagenPackCard";
import type { CurrentUser } from "@/lib/auth";

export function UnterlagenOverviewSection({
  orderId,
  auftragsnummer,
  user,
  groups,
  initialOpen = false,
  initialStuecklisteId = null,
  onChanged,
  onStuecklisteEditorOpenChange,
}: {
  orderId: number;
  auftragsnummer?: string | null;
  user: CurrentUser | null;
  groups: string[];
  initialOpen?: boolean;
  initialStuecklisteId?: number | null;
  onChanged?: () => void;
  onStuecklisteEditorOpenChange?: (open: boolean) => void;
}) {
  const [montageItems, setMontageItems] = useState<Montageunterlage[] | null>(null);
  const [werkstattItems, setWerkstattItems] = useState<Montageunterlage[] | null>(null);
  const [aufmassItems, setAufmassItems] = useState<Montageunterlage[] | null>(null);
  const [aufmassSheetCount, setAufmassSheetCount] = useState(0);
  const [stuecklisten, setStuecklisten] = useState<Stueckliste[]>([]);
  const [hasAb, setHasAb] = useState(false);
  const [printLog, setPrintLog] = useState<CoverPrintLogEntry[] | null>(null);
  const [montageOpen, setMontageOpen] = useState(false);
  const [werkstattOpen, setWerkstattOpen] = useState(false);
  const [aufmassOpen, setAufmassOpen] = useState(false);
  const [montageTab, setMontageTab] = useState<string | null>(null);
  const [werkstattTab, setWerkstattTab] = useState<string | null>(null);
  const [aufmassTab, setAufmassTab] = useState<string | null>(null);

  useEffect(() => {
    if (initialOpen || initialStuecklisteId != null) {
      setMontageOpen(true);
    }
  }, [initialOpen, initialStuecklisteId, orderId]);

  const reload = useCallback(async () => {
    try {
      const [montage, werkstatt, aufmass, form, sl, ab] = await Promise.all([
        getMontageunterlagen(orderId),
        getWerkstattunterlagen(orderId),
        getAufmassunterlagen(orderId).catch(() => [] as Montageunterlage[]),
        getAuftragAufmass(orderId).catch(() => null),
        listAuftragStuecklisten(orderId).catch(() => [] as Stueckliste[]),
        getMontageAbAvailability(orderId).catch(() => ({ exists: false })),
      ]);
      const montageList = Array.isArray(montage) ? montage : [];
      setMontageItems(
        montageList.filter((item) => !isWerkstattPackItem(item) && !isAufmassPackItem(item)),
      );
      setWerkstattItems(Array.isArray(werkstatt) ? werkstatt : []);
      setAufmassItems(Array.isArray(aufmass) ? aufmass : []);
      setAufmassSheetCount(Array.isArray(form?.sheets) ? form.sheets.length : 0);
      setStuecklisten(Array.isArray(sl) ? sl : []);
      setHasAb(Boolean(ab?.exists));
    } catch {
      setMontageItems([]);
      setWerkstattItems([]);
      setAufmassItems([]);
      setAufmassSheetCount(0);
      setStuecklisten([]);
      setHasAb(false);
    }
  }, [orderId]);

  const reloadPrintLog = useCallback(async () => {
    try {
      const list = await getCoverPrintLog(orderId);
      setPrintLog(Array.isArray(list) ? list : []);
    } catch {
      setPrintLog(null);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
    void reloadPrintLog();
  }, [reload, reloadPrintLog]);

  useEffect(() => {
    if (!montageOpen && !werkstattOpen && !aufmassOpen) void reload();
  }, [montageOpen, werkstattOpen, aufmassOpen, reload]);

  useEffect(() => {
    function onUnterlagenChanged() {
      void reload();
    }
    function onPrintLogged() {
      void reloadPrintLog();
    }
    window.addEventListener("montageunterlagen_changed", onUnterlagenChanged);
    window.addEventListener("auftraege_changed", onUnterlagenChanged);
    window.addEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onPrintLogged);
    return () => {
      window.removeEventListener("montageunterlagen_changed", onUnterlagenChanged);
      window.removeEventListener("auftraege_changed", onUnterlagenChanged);
      window.removeEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onPrintLogged);
    };
  }, [reload, reloadPrintLog]);

  const canManage = userCanManageMontageunterlagen(user, groups);
  const lastPrint = latestCoverPrint(printLog);
  const loading = montageItems === null || werkstattItems === null || aufmassItems === null;

  return (
    <>
      <OverviewCard title="Aufmaß, Werkstatt & Montage">
        <div className="eh-po-unterlagen-grid">
          <UnterlagenPackCard
            pack="aufmass"
            items={aufmassItems}
            stuecklisten={[]}
            lead={
              aufmassSheetCount > 0
                ? {
                    title: "Brandschutztüren",
                    subtitle: aufmassSheetCount === 1 ? "1 Aufmaßblatt" : `${aufmassSheetCount} Aufmaßblätter`,
                    tabKey: "aufmass-blatt",
                  }
                : null
            }
            loading={loading}
            canManage={canManage}
            onOpen={(tabKey) => {
              setAufmassTab(tabKey);
              setAufmassOpen(true);
            }}
          />
          <UnterlagenPackCard
            pack="werkstatt"
            items={werkstattItems}
            stuecklisten={stuecklisten}
            loading={loading}
            canManage={canManage}
            onOpen={(tabKey) => {
              setWerkstattTab(tabKey);
              setWerkstattOpen(true);
            }}
          />
          <UnterlagenPackCard
            pack="montage"
            items={montageItems}
            stuecklisten={stuecklisten}
            hasAb={hasAb}
            loading={loading}
            canManage={canManage}
            onOpen={(tabKey) => {
              setMontageTab(tabKey);
              setMontageOpen(true);
            }}
          />
        </div>
        {printLog !== null ? (
          <p className={lastPrint ? "eh-po-unterlagen-print eh-po-unterlagen-print--ok" : "eh-po-unterlagen-print"}>
            {lastPrint
              ? `${coverPrintKindLabel(lastPrint.cover_kind)} · ${formatDateTime(lastPrint.created_at)} · ${coverPrintActor(lastPrint)} · ${coverPrintPlace(lastPrint)}`
              : "Noch kein Deckblatt gedruckt."}
          </p>
        ) : null}
      </OverviewCard>

      <AufmassDialog
        open={aufmassOpen}
        orderId={orderId}
        user={user}
        groups={groups}
        initialTabKey={aufmassTab}
        onClose={() => {
          setAufmassOpen(false);
          setAufmassTab(null);
        }}
      />
      <MontageunterlagenDialog
        open={werkstattOpen}
        orderId={orderId}
        auftragsnummer={auftragsnummer}
        user={user}
        groups={groups}
        pack="werkstatt"
        initialTabKey={werkstattTab}
        onClose={() => {
          setWerkstattOpen(false);
          setWerkstattTab(null);
        }}
        onProjectChanged={onChanged}
        onStuecklisteEditorOpenChange={onStuecklisteEditorOpenChange}
      />
      <MontageunterlagenDialog
        open={montageOpen}
        orderId={orderId}
        auftragsnummer={auftragsnummer}
        user={user}
        groups={groups}
        initialComposerMode={initialStuecklisteId != null ? "stueckliste" : null}
        initialStuecklisteId={initialStuecklisteId}
        initialTabKey={montageTab}
        onClose={() => {
          setMontageOpen(false);
          setMontageTab(null);
        }}
        onProjectChanged={onChanged}
        onStuecklisteEditorOpenChange={onStuecklisteEditorOpenChange}
      />
    </>
  );
}
